import React, {useMemo} from 'react';

import {Box, Dialog, DialogContent, Icon, Typography} from '@mui/material';

import {COLORS} from '@/styles/colors';

import {AppButton, AppButtonVariant} from '../AppButton';

import './styles';

import ScrollToBottomButton from './components/ScrollToBottomButton/ScrollToBottomButton';
import {DEFAULT_THRESHOLD, TC_STEPS} from './models/constants';
import {useActions} from './useActions';

interface TermsAndConditionsProps {
  open: boolean;
  onClose: () => void;
}

const TermsAndConditionsModal: React.FC<TermsAndConditionsProps> = ({
  open,
  onClose,
}) => {
  const {
    indexActive,
    total,
    current,
    canContinue,
    seen,
    handleScroll,
    handleNext,
    handleBack,
    scrollToBottom,
    scrollableContentRef,
    isMobile,
    t,
    isScrollButtonVisible,
  } = useActions({
    open,
    onClose,
    steps: TC_STEPS,
    scrollThreshold: DEFAULT_THRESHOLD,
  });

  const StepComp = useMemo(
    () => TC_STEPS[indexActive].Component,
    [indexActive],
  );

  const stepIndicators = useMemo(
    () =>
      TC_STEPS.map((item, idx) => (
        <Box
          key={item.id}
          className={`step ${idx === indexActive ? 'active' : ''} ${seen[idx] ? 'done' : ''}`}
        />
      )),
    [indexActive, seen],
  );

  const paperProps = useMemo(
    () => ({
      sx: {maxWidth: '700px', width: '700px', bgcolor: 'white'},
      style: isMobile
        ? {width: '90%', margin: 0, maxWidth: 'none', height: '100vh'}
        : {},
    }),
    [isMobile],
  );

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      fullWidth
      maxWidth={isMobile ? false : 'sm'}
      PaperProps={paperProps}>
      <DialogContent sx={{overflow: 'hidden', position: 'relative'}}>
        <Box className="modal-container">
          <Box className="stepper">{stepIndicators}</Box>

          <Box
            id="terms-and-conditions-scrollable-content"
            className="scrollable-content relative"
            ref={scrollableContentRef}
            onScroll={handleScroll}
            sx={{
              width: '100%',
              height: isMobile ? '60vh' : '70vh',
              overflowY: 'auto',
            }}>
            <StepComp />
            <ScrollToBottomButton
              onClick={scrollToBottom}
              visible={isScrollButtonVisible}
            />
          </Box>

          <Typography className="modal-footer-text">
            {t('terms_and_conditions.clicking_continue')}
          </Typography>

          <Box className="buttons-container">
            <Box>
              {indexActive > 0 && (
                <AppButton
                  id="terms-and-conditions-back-button"
                  onClick={handleBack}
                  variant={AppButtonVariant.onText}
                  iconLeft={
                    <Icon sx={{fontSize: 20, color: COLORS.ON_SURFACE_1}}>
                      arrow_back
                    </Icon>
                  }
                />
              )}
            </Box>
            <Box>
              <AppButton
                variant={AppButtonVariant.onPrimary}
                id="terms-and-conditions-continue-button"
                disabled={!canContinue}
                onClick={handleNext}
                label={`${current}/${total} ${t('terms_and_conditions.button')}`}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(TermsAndConditionsModal);
